import React, { useEffect, useState } from 'react'
import usePlacesAutocomplete from 'use-places-autocomplete'

import Geocoder from '@js/common/geocoder'

import FormText from '@f/elements/FormText/FormText'

import { useDocumentLoaded } from '@f-fertighaus/utils/useDocumentLoaded'

const FilterLocation = ({
  placeholder = 'z.B. 12345 oder Berlin',
  className,
  inputClassName,
  onChange,
  ...props
}) => {
  const [requestNewSessionToken, setRequestNewSessionToken] = useState(true)
  const [sessionToken, setSessionToken] = useState()
  const [defaultValue, setDefaultValue] = useState(props.locationParams)
  const documentLoaded = useDocumentLoaded()

  const getSessionToken = () => sessionToken

  useEffect(() => {
    if (!documentLoaded) {
      return
    }
    if (requestNewSessionToken) {
      setSessionToken(new google.maps.places.AutocompleteSessionToken())
      setRequestNewSessionToken(false)
    }
  }, [requestNewSessionToken, documentLoaded])

  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['(regions)'],
      componentRestrictions: {
        country: ['de', 'at']
      },
      sessionToken: getSessionToken()
    },
    callbackName: 'initMap',
    debounce: 300
  })

  const handleInput = event => {
    setValue(event.target.value)
    setDefaultValue(event.target.value)
  }

  const handleSelect = suggestion => () => {
    if (suggestion) {
      let geocoder = new Geocoder()
      geocoder
        .getLocationFromPlaceID({
          ...suggestion,
          sessionToken: getSessionToken()
        })
        .then(
          data => {
            if (data.postcode && data.country) {
              setValue(
                '',
                false
              ) /* false is there so emptying the field doesn't trigger an autocomplete request */
              clearSuggestions()
              setRequestNewSessionToken(true)
              setHighlightedSuggestion(0)

              const { label, location } = data

              setDefaultValue({
                label,
                location
              })

              props.setLocationParams({
                label,
                location
              })

              if (onChange) {
                onChange(data)
              }
            }
          },
          () => {}
        )
    }
  }

  const [highlightedSuggestion, setHighlightedSuggestion] = useState(0)
  const handleKeyDown = event => {
    switch (event.keyCode) {
      case 40:
        if (status === 'OK' && data.length > 1) {
          setHighlightedSuggestion(s => (s < data.length - 1 ? s + 1 : 0))
          event.preventDefault()
        }
        break
      case 38:
        if (status === 'OK') {
          setHighlightedSuggestion(s => (s > 0 ? s - 1 : 0))
          event.preventDefault()
        }
        break
      case 13:
        /* Pressing enter sets the highlighed suggestion if there's one */
        if (status === 'OK' && data[highlightedSuggestion]) {
          handleSelect(data[highlightedSuggestion])()
          event.preventDefault()
          event.target.blur()
        }
        break
    }
  }

  const clickOutside = event => {
    if (!event.target.closest('.LocationFinder')) {
      setValue(
        '',
        false
      ) /* false is there so emptying the field doesn't trigger an autocomplete request */
      clearSuggestions()
      setRequestNewSessionToken(true)
    }
  }

  useEffect(() => {
    document.addEventListener('click', clickOutside)

    return () => document.removeEventListener('click', clickOutside)
  }, [])

  const renderSuggestions = () =>
    data.map((suggestion, suggestionIndex) => {
      const { id, description, matched_substrings } = suggestion

      const beforeDescription = description.substr(
        0,
        matched_substrings[0].offset
      )
      const matchedDescription = description.substr(
        beforeDescription.length,
        matched_substrings[0].length
      )
      const afterDescription = description.substr(
        beforeDescription.length + matchedDescription.length,
        description.length -
          (beforeDescription.length + matchedDescription.length)
      )

      return (
        <li
          key={`${suggestionIndex}_${id ? id : '_suggestion'}`}
          onClick={handleSelect(suggestion)}
          className={`LocationFinder-suggestions-item ${
            suggestionIndex === highlightedSuggestion ? 'f-c-fo-ter' : ''
          }`}>
          {beforeDescription}
          <strong>{matchedDescription}</strong>
          {afterDescription}
        </li>
      )
    })

  return (
    <span className={`LocationFinder ${className ? className : ''}`}>
      <FormText
        inputField
        value={defaultValue.label}
        onChange={handleInput}
        disabled={!ready}
        className={`[ f-c-bo-neu025 ] FormText-wrapper has-border is-full-width ${
          inputClassName ? inputClassName : ''
        }`}
        placeholder={placeholder}
        locationFinderPlaceholder={defaultValue.label}
        inputProps={{ onKeyDown: handleKeyDown }}
      />
      {/* We can use the "status" to decide whether we should display the dropdown or not */}
      {status === 'OK' && (
        <ul className='[ f!-list f-pos-ab ] LocationFinder-suggestions'>
          {renderSuggestions()}
        </ul>
      )}
    </span>
  )
}

export default FilterLocation
